/**
 * Canonical pricing ladder: single source of truth for plan_id, display name, and USD amounts.
 * Used by the pricing UI and checkout so selected plan, billing term, and amount stay in sync.
 */

export const CANONICAL_PLAN_IDS = [
  'solve_assessment',
  'casey_assessment',
  'assessments_bundle',
  'mckinsey_ai_coach_only',
  'bcg_ai_coach_only',
  'mckinsey_track_full',
  'bcg_track_full',
  'all_access',
] as const;

export type PlanId = (typeof CANONICAL_PLAN_IDS)[number];

/** Billing term as sent to PayFast and stored (not UI "sixMo") */
export const BILLING_TERMS = ['monthly', 'six_month'] as const;
export type BillingTerm = (typeof BILLING_TERMS)[number];

/** Map UI billing toggle value to PayFast billing_term */
export const BILLING_UI_TO_PAYFAST: Record<'monthly' | 'sixMo', BillingTerm> = {
  monthly: 'monthly',
  sixMo: 'six_month',
};

export interface PricingPlan {
  plan_id: PlanId;
  name: string;
  product_family: 'mckinsey' | 'bcg' | 'bundle';
  monthly: number;
  six_month: number;
}

/** Canonical pricing (USD). Align six_month key with backend billing_term. */
export const PRICING_PLANS: PricingPlan[] = [
  { plan_id: 'solve_assessment', name: 'Solve Assessment', product_family: 'mckinsey', monthly: 44.99, six_month: 69.99 },
  { plan_id: 'casey_assessment', name: 'Casey Assessment', product_family: 'bcg', monthly: 39.99, six_month: 59.99 },
  { plan_id: 'assessments_bundle', name: 'Assessments Bundle', product_family: 'bundle', monthly: 54.99, six_month: 119.99 },
  { plan_id: 'mckinsey_ai_coach_only', name: 'McKinsey AI Coach Only', product_family: 'mckinsey', monthly: 45.99, six_month: 119.99 },
  { plan_id: 'bcg_ai_coach_only', name: 'BCG AI Coach Only', product_family: 'bcg', monthly: 45.99, six_month: 119.99 },
  { plan_id: 'mckinsey_track_full', name: 'McKinsey Track (Full)', product_family: 'mckinsey', monthly: 69.99, six_month: 149.99 },
  { plan_id: 'bcg_track_full', name: 'BCG Track (Full)', product_family: 'bcg', monthly: 59.99, six_month: 139.99 },
  { plan_id: 'all_access', name: 'All-Access', product_family: 'bundle', monthly: 99.99, six_month: 199.99 },
];

const PLANS_BY_ID = new Map<string, PricingPlan>(PRICING_PLANS.map((p) => [p.plan_id, p]));

export function getPlanById(planId: string | null | undefined): PricingPlan | null {
  if (!planId) return null;
  return PLANS_BY_ID.get(planId) ?? null;
}

export function getAmountUsd(planId: string | null | undefined, billingTerm: BillingTerm | string | null | undefined): number | null {
  const plan = getPlanById(planId);
  if (!plan) return null;
  if (billingTerm === 'monthly') return plan.monthly;
  if (billingTerm === 'six_month') return plan.six_month;
  return null;
}

export function isValidPlanId(planId: string): planId is PlanId {
  return CANONICAL_PLAN_IDS.includes(planId as PlanId);
}

export function isValidBillingTerm(term: string): term is BillingTerm {
  return BILLING_TERMS.includes(term as BillingTerm);
}
